import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Eye, Edit, Trash2, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';

interface TipoProposta {
  id: string;
  nome: string;
  descricao?: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export default function TipoProposta() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission, refetch } = usePermissions();
  
  const [tipos, setTipos] = useState<TipoProposta[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedTipo, setSelectedTipo] = useState<TipoProposta | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    nome: '',
    descricao: ''
  });

  const canRead = hasPermission('recursos_humanos', 'tipo_proposta', 'read');
  const canWrite = hasPermission('recursos_humanos', 'tipo_proposta', 'write');
  const canAdmin = hasPermission('recursos_humanos', 'tipo_proposta', 'admin');

  const fetchTipos = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('tipo_proposta')
        .select('*')
        .eq('status', 'ativo');

      if (searchTerm) {
        query = query.or(`nome.ilike.%${searchTerm}%,descricao.ilike.%${searchTerm}%`);
      }

      query = query.order(sortBy, { ascending: sortOrder === 'asc' });
      
      const { data, error } = await query;
      if (error) throw error;
      
      setTipos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar tipos de proposta",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked) return;
    if (!canRead) {
      navigate('/intranet');
      return;
    }
    fetchTipos();
  }, [canRead, permissionsChecked, sortBy, sortOrder]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleSearch = () => {
    fetchTipos();
  };

  const handleCreate = async () => {
    try {
      const { error } = await supabase
        .from('tipo_proposta')
        .insert([{
          ...formData,
          created_by: user?.id
        }]);

      if (error) throw error;

      toast({
        title: "Tipo de proposta criado com sucesso!",
      });

      setShowCreateDialog(false);
      setFormData({ nome: '', descricao: '' });
      fetchTipos();
    } catch (error: any) {
      toast({
        title: "Erro ao criar tipo de proposta",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = async () => {
    if (!selectedTipo) return;

    try {
      const { error } = await supabase
        .from('tipo_proposta')
        .update({
          ...formData,
          updated_by: user?.id
        })
        .eq('id', selectedTipo.id);

      if (error) throw error;

      toast({
        title: "Tipo de proposta atualizado com sucesso!",
      });

      setShowEditDialog(false);
      setSelectedTipo(null);
      setFormData({ nome: '', descricao: '' });
      fetchTipos();
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar tipo de proposta",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tipo_proposta')
        .update({ 
          status: 'inativo',
          updated_by: user?.id
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Tipo de proposta excluído com sucesso!",
      });

      fetchTipos();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir tipo de proposta",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (!permissionsChecked || !canRead) {
    return (
      <div className="p-6">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Acesso Negado</h2>
          <p className="text-muted-foreground">
            Você não tem permissão para visualizar tipos de proposta.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Tipos de Proposta</h1>
          <p className="text-muted-foreground">Gerencie os tipos de proposta do sistema</p>
        </div>
        
        {canWrite && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Novo Tipo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Tipo de Proposta</DialogTitle>
                <DialogDescription>
                  Preencha os dados para criar um novo tipo de proposta.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Digite o nome do tipo"
                  />
                </div>
                <div>
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Digite uma descrição (opcional)"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreate} disabled={!formData.nome.trim()}>
                    Criar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Input
            placeholder="Pesquisar tipos de proposta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="max-w-sm"
          />
          <Button onClick={handleSearch}>
            <Search className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : tipos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {searchTerm ? 'Nenhum resultado encontrado.' : 'Nenhum tipo de proposta cadastrado.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('nome')}
                >
                  <div className="flex items-center">
                    Nome
                    {sortBy === 'nome' && (
                      sortOrder === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center">
                    Data de Criação
                    {sortBy === 'created_at' && (
                      sortOrder === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tipos.map((tipo) => (
                <TableRow key={tipo.id}>
                  <TableCell className="font-medium">{tipo.nome}</TableCell>
                  <TableCell>{tipo.descricao || '-'}</TableCell>
                  <TableCell>{format(new Date(tipo.created_at), 'dd/MM/yyyy')}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedTipo(tipo);
                          setShowViewDialog(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTipo(tipo);
                            setFormData({ nome: tipo.nome, descricao: tipo.descricao || '' });
                            setShowEditDialog(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      
                      {canAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja excluir o tipo "{tipo.nome}"? Esta ação não pode ser desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(tipo.id)}>
                                Confirmar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tipo de Proposta</DialogTitle>
            <DialogDescription>
              Altere os dados do tipo de proposta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-nome">Nome *</Label>
              <Input
                id="edit-nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Digite o nome do tipo"
              />
            </div>
            <div>
              <Label htmlFor="edit-descricao">Descrição</Label>
              <Textarea
                id="edit-descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Digite uma descrição (opcional)"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEdit} disabled={!formData.nome.trim()}>
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalhes do Tipo de Proposta</DialogTitle>
          </DialogHeader>
          {selectedTipo && (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <p className="text-sm mt-1">{selectedTipo.nome}</p>
              </div>
              <div>
                <Label>Descrição</Label>
                <p className="text-sm mt-1">{selectedTipo.descricao || 'Não informado'}</p>
              </div>
              <div>
                <Label>Status</Label>
                <div className="mt-1">
                  <Badge variant={selectedTipo.status === 'ativo' ? "default" : "secondary"}>
                    {selectedTipo.status === 'ativo' ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </div>
              <div>
                <Label>Data de Criação</Label>
                <p className="text-sm mt-1">{format(new Date(selectedTipo.created_at), 'dd/MM/yyyy HH:mm')}</p>
              </div>
              <div>
                <Label>Última Atualização</Label>
                <p className="text-sm mt-1">{format(new Date(selectedTipo.updated_at), 'dd/MM/yyyy HH:mm')}</p>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}